import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  input,
  OnChanges,
  output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { Router } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AppNotification } from '../../../../models/notifications/app-notification';
import { FeatureAvailabilityService } from '../../../../services/feature-availability.service';
import { NavService } from '../../../../services/nav.service';
import { LayoutStore } from '../../../../store/layout.store';
import { NotificationStore } from '../../../../store/notification.store';
import { RouterFacade } from '../../../../store/router.facade';
import { IconComponent } from '../../../base-components/icon/icon.component';
import { NavItem } from './nav-item';

@Component({
  selector: 'app-nav-item',
  imports: [
    TranslateModule,
    MatIconModule,
    MatListModule,
    CommonModule,
    IconComponent,
  ],
  templateUrl: './nav-item.component.html',
  styleUrl: './nav-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppNavItemComponent implements OnChanges {
  navService = inject(NavService);
  routerFacade = inject(RouterFacade);
  router = inject(Router);
  featureAvailabilityService = inject(FeatureAvailabilityService);
  notificationStore = inject(NotificationStore);
  translateService = inject(TranslateService);
  layoutStore = inject(LayoutStore);

  public expanded = false;
  public disabled = false;
  public twoLines = false;
  public iconHoverState = 'default';

  public selectedIcon = input<string>();
  public item = input<NavItem>();
  public depth = input<number>(0);

  public toggleMobileLink = output<void>();
  public notify = output<boolean>();

  @HostBinding('attr.aria-expanded') ariaExpanded = this.expanded;
  @HostBinding('attr.data-depth') get hostDepth() {
    return this.depth();
  }


  ngOnChanges() {
    const url = this.navService.currentUrl();
    const item = this.item();
    if (item?.route && url) {
      this.expanded = url.indexOf(`/${item.route}`) === 0;
      this.ariaExpanded = this.expanded;
    }
  }

  onItemSelected(item: NavItem) {
    if (item.disabled) {
      this.handleDisabledItemClick(item);
      return;
    }

    // Handle action-based menu items
    if (item.action) {
      this.handleAction(item.action);
      if (window.innerWidth < 1024) {
        this.notify.emit(true);
      }
      return;
    }

    if (!item.children || !item.children.length) {
      this.routerFacade.navigate([item.route]);
    }
    if (item.children && item.children.length) {
      this.expanded = !this.expanded;
    }

    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    if (!this.expanded) {
      if (window.innerWidth < 1024) {
        this.notify.emit(true);
      }
    }
  }

  private handleAction(action: string): void {
    switch (action) {
      case 'openVocalAssistant':
        this.layoutStore.openFullPanelModal('vocal-assistant');
        break;
    }
  }

  private handleDisabledItemClick(item: NavItem) {
    if (item.displayName) {
      this.featureAvailabilityService.showFeatureAvailabilityDialog(item.displayName)
        .subscribe(result => {
          if (result?.notify) {
            const translatedFeatureName = this.translateService.instant(item.displayName);

            const notification = new AppNotification({
              summary: 'notifications.featureNotification.title',
              detail: 'notifications.featureNotification.message',
              arguments: { feature: translatedFeatureName }
            });
            notification.severity = 'success';

            this.notificationStore.addNotification(notification);
          }
        });
    }
  }

  onSubItemSelected(item: NavItem) {
    if (!item.children || !item.children.length) {
      if (this.expanded && window.innerWidth < 1024) {
        this.notify.emit(true);
      }
    }
  }

  onIconMouseEnter() {
    this.iconHoverState = 'hovered';
  }

  onIconMouseLeave() {
    this.iconHoverState = 'default';
  }
}
